import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, MapPin, Package, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { Badge } from '@components/Card';
import { driverRouteService } from '@services/driverRouteService';
import { DriverRoute } from '@/types/driverRoute';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  // Strip Z/offset: backend returns Brasília local time, no UTC conversion should occur
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleDateString('pt-BR'); } catch { return dateStr; }
};

export const DeliveryExecutionListView: React.FC = () => {
  const navigate = useNavigate();

  const [routes, setRoutes] = useState<DriverRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  const loadRoutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await driverRouteService.getPendingRoutes({
        page: currentPage,
        pageSize,
      });
      setRoutes(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar rotas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes();
  }, [currentPage]);

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center sm:text-left">
        <h1 className="text-h1 flex items-center justify-center sm:justify-start gap-2">
          <Truck size={24} /> Entregas Pendentes
        </h1>
        <p className="text-slate-600 text-sm">Selecione uma rota para iniciar a entrega</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Carregando rotas...</div>
      ) : routes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle size={48} className="mx-auto mb-3 text-emerald-400" />
            <p className="text-lg font-medium text-slate-700">Nenhuma entrega pendente</p>
            <p className="text-sm text-slate-500 mt-1">Todas as rotas foram concluídas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {routes.map((route) => {
            const pending = route.pending_stops_count ?? route.stops?.filter((s) => !s.is_delivered).length ?? 0;
            const total = route.total_stops_count ?? route.stops?.length ?? 0;

            return (
              <Card
                key={route.driver_route_id}
                className="cursor-pointer hover:shadow-md transition-shadow active:bg-slate-50"
                onClick={() => navigate(`/delivery/${route.driver_route_id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Truck size={24} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">Rota #{route.driver_route_id}</span>
                        <Badge variant="warning">{pending} pendente(s)</Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">
                        {route.driver?.full_name || `Motorista #${route.driver_id}`}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Package size={12} /> {route.vehicle?.description || `Veículo #${route.vehicle_id}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {total} parada(s)
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {formatDate(route.created_at)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="flex items-center px-3 text-sm text-slate-600">
            {currentPage}/{totalPages}
          </span>
          <Button variant="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
};
